import React, { useState, useEffect, useRef } from "react";
import { flushSync } from "react-dom";
import { ActionIcon, Button, CloseButton, Menu, Paper, Stack, TextInput } from "@mantine/core";
import TagBadge, { tagBadgeColor, tagName } from "./TagBadge";
import axiosInstance from './../axiosInstance.js';

// Tag types, mirrored from logic/tag/tag.go.
export const TAG_TYPE = {
  Unknown: 0,
  CustomUserCreated: 1,
  PersonalRecommendationUser: 2,
  GlobalSelectable: 3,
  UnselectableKind: 4,
  UnselectableGenre: 5,
  UnselectableSubCategory: 6,
  UnselectableYear: 7,
};

// Row visibility: only manual user-owned types render as removable pills.
// Type 3 (GlobalSelectable) is the top pill; 4-7 are auto and hidden.
const isManualRowType = (type) =>
  type === TAG_TYPE.Unknown ||
  type === TAG_TYPE.CustomUserCreated ||
  type === TAG_TYPE.PersonalRecommendationUser;
const isGlobalManualType = (type) => type === TAG_TYPE.GlobalSelectable;

// Global-manual tag catalog. Ids must stay in sync with tag.go:44-56.
// Order matches the on-screen sequence.
export const GLOBAL_MANUAL_TAGS = [
  { id: "c1da3f9e-96b7-4ed6-a4ba-0b2f6f2c009a", name: "like",       type: TAG_TYPE.GlobalSelectable },
  { id: "0d89ae4d-1c88-4b0f-8e7c-90cead95775f", name: "veryGood",   type: TAG_TYPE.GlobalSelectable },
  { id: "30f6819a-b208-40c9-a119-e334876450f9", name: "good",       type: TAG_TYPE.GlobalSelectable },
  { id: "b0bfe30b-4058-4f71-8381-80f4b41e69fa", name: "finished",   type: TAG_TYPE.GlobalSelectable },
  { id: "a950dea3-7785-411b-8a03-fca51f0783ec", name: "bad",        type: TAG_TYPE.GlobalSelectable },
  { id: "9c2a4b7e-6f31-4a2d-b8c4-e5f0d1a9b3c7", name: "noRating",   type: TAG_TYPE.GlobalSelectable },
  { id: "1fbf99af-5735-4fd9-9e7c-40a1b15bf004", name: "inProgress", type: TAG_TYPE.GlobalSelectable },
  { id: "bc1c0e81-97f6-4609-b359-72e948b4c83c", name: "todo",       type: TAG_TYPE.GlobalSelectable },
];
const GLOBAL_MANUAL_NAMES = new Set(GLOBAL_MANUAL_TAGS.map(t => t.name));

// Legacy string-list callers (FollowingModal via eventTagNames) wrap to objects.
const nameToObject = (name) => ({
  name,
  type: GLOBAL_MANUAL_NAMES.has(name) ? TAG_TYPE.GlobalSelectable : TAG_TYPE.Unknown,
});
const normalizeTags = (input) =>
  (input || []).map(t => (typeof t === 'string' ? nameToObject(t) : t));

const TagsInput = ({
  tags: tagsProp,                 // array of tag objects; parent owns the source of truth
  onAddCustomTag,                 // (name: string) => Promise|void — parent hits POST /customTag
  onRemoveCustomTag,              // (tagId: string) => Promise|void — parent hits DELETE /customTag
  onSetGlobalManualTag,           // (tagObject: {id,name,type}) => Promise|void — parent hits POST /globalManualTag (or POST /artEvents if no event yet)
  editable = true,
  language,
  reducedView = false,
  flush = false,
  singleRow = false,
  artCategory,
}) => {
  const tags = normalizeTags(tagsProp);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isInputVisible, setIsInputVisible] = useState(false);
  const [isGlobalManualTagDropdownVisible, setIsGlobalManualTagDropdownVisible] = useState(false);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const selectedGlobalManualTag = tags.find(t => isGlobalManualType(t.type)) || null;
  const attachedNames = new Set(tags.map(t => t.name));

  const handleSearch = async (query) => {
    setSearchQuery(query);
    try {
      const response = await axiosInstance.get(`/relevant/tags/search?query=${query}`);
      setSearchResults((response.data || []).filter(item => !attachedNames.has(item.name)));
      setIsDropdownVisible(true);
    } catch (error) {
      console.error("Error fetching search results:", error);
    }
  };

  const handleInputChange = (e) => { handleSearch(e.target.value); };

  const submitCustomTagName = async (name) => {
    setSearchQuery("");
    setSearchResults([]);
    setIsDropdownVisible(false);
    if (!name || attachedNames.has(name)) return;
    if (onAddCustomTag) await onAddCustomTag(name);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsDropdownVisible(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isInputVisible) return;
    const id = requestAnimationFrame(() => {
      if (inputRef.current && typeof inputRef.current.focus === 'function') {
        inputRef.current.focus();
      }
    });
    return () => cancelAnimationFrame(id);
  }, [isInputVisible]);

  const renderTagLabel = (tag) => (
    tag.imgUrl
      ? (
        <div className="flex items-center text-center gap-1">
          #
          <img className="w-5 max-w-none h-5 rounded-full" src={tag.imgUrl} alt="" />
          {tagName(tag.name, language)}
        </div>
      )
      : <>{tagName(tag.name, language, artCategory)}</>
  );

  return (
    <div className={`relative ${flush ? '' : 'px-2 md:px-4'}`}>
      <div
        className={singleRow
          ? "flex flex-nowrap items-center gap-1 overflow-x-auto no-scrollbar [&>*]:flex-shrink-0"
          : "flex justify-center flex-wrap items-center gap-1"}
        style={singleRow ? { WebkitOverflowScrolling: 'touch' } : undefined}
      >
        {editable ? (
          tags.length > 0 && selectedGlobalManualTag ? (
            <Menu
              opened={isGlobalManualTagDropdownVisible}
              onChange={setIsGlobalManualTagDropdownVisible}
              position="bottom-start"
              withinPortal
              shadow="md"
              radius="md"
            >
              <Menu.Target>
                <TagBadge
                  component="button"
                  type="button"
                  className="cursor-pointer"
                  radius="md"
                  size="md"
                  color={tagBadgeColor(selectedGlobalManualTag.name)}
                  variant="filled"
                  rightSection={<span className="text-[10px] opacity-70">{isGlobalManualTagDropdownVisible ? '▲' : '▼'}</span>}
                  onClick={() => setIsGlobalManualTagDropdownVisible(!isGlobalManualTagDropdownVisible)}
                >
                  {tagName(selectedGlobalManualTag.name, language, artCategory)}
                </TagBadge>
              </Menu.Target>
              <Menu.Dropdown p={4}>
                <Stack gap={4}>
                  {GLOBAL_MANUAL_TAGS
                    .filter(t => t.name !== selectedGlobalManualTag.name)
                    .map((t) => (
                      <TagBadge
                        key={t.id}
                        component="button"
                        type="button"
                        radius="md"
                        size="md"
                        className="cursor-pointer"
                        color={tagBadgeColor(t.name)}
                        variant="filled"
                        onClick={() => {
                          setIsGlobalManualTagDropdownVisible(false);
                          if (onSetGlobalManualTag) onSetGlobalManualTag(t);
                        }}
                      >
                        {tagName(t.name, language, artCategory)}
                      </TagBadge>
                    ))}
                </Stack>
              </Menu.Dropdown>
            </Menu>
          ) : (
            GLOBAL_MANUAL_TAGS.map((t) => {
              const selected = selectedGlobalManualTag && selectedGlobalManualTag.name === t.name;
              return (
                <TagBadge
                  key={t.id}
                  component="button"
                  type="button"
                  radius="md"
                  size="md"
                  className="cursor-pointer"
                  color={selected ? tagBadgeColor(t.name) : "gray"}
                  variant="filled"
                  style={selected
                    ? { transform: 'scale(1.08)', boxShadow: '0 0 0 2px var(--mantine-color-body), 0 0 0 4px var(--mantine-color-text)', transition: 'transform 0.15s, box-shadow 0.15s, filter 0.15s' }
                    : { transition: 'box-shadow 0.15s, filter 0.15s' }}
                  onMouseEnter={(e) => {
                    if (selected) return;
                    e.currentTarget.style.filter = 'brightness(1.15)';
                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.18)';
                  }}
                  onMouseLeave={(e) => {
                    if (selected) return;
                    e.currentTarget.style.filter = '';
                    e.currentTarget.style.boxShadow = '';
                  }}
                  onClick={() => { if (onSetGlobalManualTag) onSetGlobalManualTag(t); }}
                >
                  {tagName(t.name, language, artCategory)}
                </TagBadge>
              );
            })
          )
        ) : (
          selectedGlobalManualTag && (
            <TagBadge
              radius="md"
              size="md"
              color={tagBadgeColor(selectedGlobalManualTag.name)}
              variant="filled"
              className="cursor-pointer"
            >
              {tagName(selectedGlobalManualTag.name, language, artCategory)}
            </TagBadge>
          )
        )}

        {!reducedView &&
        <>
          {/* Manual user-owned tag row — types 0/1/2 only. */}
          {tags.filter(t => isManualRowType(t.type)).map((tag) => (
            <TagBadge
              key={tag.id || tag.name}
              component="span"
              radius="md"
              size="md"
              color={tagBadgeColor(tag.name)}
              variant="filled"
            >
              {renderTagLabel(tag)}
              {editable && tag.id && (
                <CloseButton
                  size="xs"
                  className="ml-1"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (onRemoveCustomTag) onRemoveCustomTag(tag.id);
                  }}
                />
              )}
            </TagBadge>
          ))}

          {(editable && selectedGlobalManualTag) && (
            <ActionIcon
              size="lg"
              radius="md"
              variant="light"
              color="gray"
              aria-label="Add tag"
              onClick={() => {
                if (!isInputVisible) {
                  // flushSync so the input is in the DOM before we focus it,
                  // otherwise mobile browsers won't open the keyboard.
                  flushSync(() => setIsInputVisible(true));
                  if (inputRef.current && typeof inputRef.current.focus === 'function') {
                    inputRef.current.focus();
                  }
                } else {
                  setIsInputVisible(false);
                }
              }}
            >
              +
            </ActionIcon>
          )}
        </>
        }
      </div>

      {isInputVisible && editable && (
        <div className="mt-1 w-full flex justify-center">
          <TextInput
            ref={inputRef}
            type="text"
            inputMode="search"
            radius="md"
            size="md"
            className="w-auto text-center"
            styles={{ input: { textAlign: "center", fontSize: 14, lineHeight: 1.4, paddingBlock: 8 } }}
            placeholder={language === "ru" ? "Введите название тэга" : "Enter tag name"}
            value={searchQuery}
            onChange={handleInputChange}
            onFocus={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" && searchQuery.trim() !== "") {
                submitCustomTagName(searchQuery.trim());
                setIsInputVisible(false);
              }
            }}
            onBlur={() => setIsInputVisible(false)}
          />
        </div>
      )}

      {isDropdownVisible && searchResults.length > 0 && (
        <Paper
          ref={dropdownRef}
          withBorder
          shadow="md"
          radius="md"
          className="absolute top-full left-0 right-0 mt-1 max-h-40 overflow-y-auto z-[60]"
        >
          <Stack gap="xs" p="xs">
            {searchResults.map((tag) => (
              <Button
                key={tag.id || tag.name}
                type="button"
                size="sm"
                variant="subtle"
                color={tagBadgeColor(tag.name)}
                radius="md"
                onMouseDown={() => submitCustomTagName(tag.name)} /* onClick doesn't fire — the document mousedown listener hides the dropdown first */
              >
                {tagName(tag.name, language, artCategory)}
              </Button>
            ))}
          </Stack>
        </Paper>
      )}
    </div>
  );
};

export const tagColor = (name, rounded = true, bgColorOverride, type = 0) => {
  const base = `font-sans font-medium tracking-wide text-[11px] px-2.5 py-0.5
               ${rounded ? "rounded-full" : ""}`;

  if (bgColorOverride) {
    return `${base} ${bgColorOverride} text-white`;
  }

  if (name === "like") {
    return `${base} bg-red-500/90 text-white shadow-sm`;
  }
  if (name === "veryGood") {
    return `${base} bg-orange-500/90 text-white shadow-sm`;
  }
  if (name === "good") {
    return `${base} bg-green-500/90 text-white shadow-sm`;
  }
  if (name === "inProgress") {
    return `${base} bg-cyan-500/90 text-white shadow-sm`;
  }
  if (name === "todo") {
    return `${base} bg-sky-300/90 text-white shadow-sm`;
  }
  if (name === "finished") {
    return `${base} bg-yellow-500/90 text-white shadow-sm`;
  }
  if (name === "bad") {
    return `${base} bg-gray-600/90 text-white shadow-sm`;
  }

  if (name === "book" || name === "movie" || name === "game" || name === "youtubechannel") {
    return `${base} bg-teal-500/80 text-white`;
  }

  if (type === 5) {
    return `${base} bg-purple-500/90 text-white shadow-sm`;
  }

  return `${base} bg-gray-500 text-white`;
};

export default TagsInput;
